import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase, SUPABASE_ANON_KEY } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/auth/AuthContext';
import FacialConsentForm, {
  FacialConsentFormData,
  initialFormData,
} from '../../components/forms/FacialConsentForm';

/**
 * FacialConsentFormPage
 *
 * Public page for filling out the facial consent form.
 * Can be accessed via:
 * 1. Direct link with token (from booking confirmation email)
 * 2. From profile page (logged in users)
 *
 * URL: /consent-form?token=xxx or /consent-form (logged in)
 */
const FacialConsentFormPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [existingFormData, setExistingFormData] = useState<Partial<FacialConsentFormData> | null>(null);
  const [existingSignatureUrl, setExistingSignatureUrl] = useState<string | null>(null);
  const [formToken, setFormToken] = useState<string | null>(null);
  const [appointmentInfo, setAppointmentInfo] = useState<{
    date: string;
    services: string[];
  } | null>(null);

  const token = searchParams.get('token');

  // Load existing form data
  useEffect(() => {
    const loadFormData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Case 1: Token provided (from email link)
        if (token) {
          const { data: formData, error: fetchError } = await supabase
            .from('facial_consent_forms')
            .select(`
              *,
              appointments (
                start_time,
                appointment_services (
                  services ( name )
                )
              )
            `)
            .eq('form_token', token)
            .single();

          if (fetchError || !formData) {
            setError('Invalid or expired form link. Please contact us for assistance.');
            setIsLoading(false);
            return;
          }

          setFormToken(token);

          // If already signed, show read-only
          if (formData.signature_url) {
            setExistingSignatureUrl(formData.signature_url);
          }

          // Extract appointment info
          if (formData.appointments) {
            const apt = formData.appointments as any;
            setAppointmentInfo({
              date: new Date(apt.start_time).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              }),
              services: apt.appointment_services?.map((as: any) => as.services?.name).filter(Boolean) || [],
            });
          }

          // Map database fields to form data
          setExistingFormData({
            firstName: formData.first_name || '',
            lastName: formData.last_name || '',
            phone: formData.phone || '',
            email: formData.email || '',
            addressStreet: formData.address_street || '',
            addressStreet2: formData.address_street2 || '',
            addressCity: formData.address_city || '',
            addressState: formData.address_state || '',
            addressZip: formData.address_zip || '',
            ...(formData.skin_data || {}),
            ...(formData.medical_data || {}),
            ...(formData.female_data || {}),
            signedAt: formData.signed_at || null,
          });
        }
        // Case 2: Logged in user
        else if (user && userProfile) {
          // Check if user has existing consent form
          const { data: existingForm } = await supabase
            .from('facial_consent_forms')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .single();

          if (existingForm) {
            setFormToken(existingForm.form_token);
            if (existingForm.signature_url) {
              setExistingSignatureUrl(existingForm.signature_url);
            }

            setExistingFormData({
              firstName: existingForm.first_name || userProfile.full_name?.split(' ')[0] || '',
              lastName: existingForm.last_name || userProfile.full_name?.split(' ').slice(1).join(' ') || '',
              phone: existingForm.phone || userProfile.phone_number || '',
              email: existingForm.email || userProfile.email || '',
              addressStreet: existingForm.address_street || userProfile.address_line1 || '',
              addressStreet2: existingForm.address_street2 || userProfile.address_line2 || '',
              addressCity: existingForm.address_city || userProfile.city || '',
              addressState: existingForm.address_state || userProfile.state || '',
              addressZip: existingForm.address_zip || userProfile.zip_code || '',
              ...(existingForm.skin_data || {}),
              ...(existingForm.medical_data || {}),
              ...(existingForm.female_data || {}),
              signedAt: existingForm.signed_at || null,
            });
          } else {
            // Pre-fill from profile
            setExistingFormData({
              firstName: userProfile.full_name?.split(' ')[0] || '',
              lastName: userProfile.full_name?.split(' ').slice(1).join(' ') || '',
              phone: userProfile.phone_number || '',
              email: userProfile.email || '',
              addressStreet: userProfile.address_line1 || '',
              addressStreet2: userProfile.address_line2 || '',
              addressCity: userProfile.city || '',
              addressState: userProfile.state || '',
              addressZip: userProfile.zip_code || '',
            });
          }
        }
        // Case 3: No token and not logged in
        else {
          // Allow blank form for walk-ins or manual entry
          setExistingFormData(initialFormData);
        }
      } catch (err) {
        console.error('Error loading form:', err);
        setError('Failed to load form. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadFormData();
  }, [token, user, userProfile]);

  // Handle form submission
  const handleSubmit = async (formData: FacialConsentFormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Get auth token if logged in
      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token;

      // Call edge function to submit form
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/submit-facial-consent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
          },
          body: JSON.stringify({
            formData,
            formToken,
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit form');
      }

      setSuccess(true);

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      console.error('Error submitting form:', err);
      setError(err.message || 'Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3A5E63] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading form...</p>
        </div>
      </div>
    );
  }

  // Error state (invalid token)
  if (error && !existingFormData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Form Not Found</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-[#3A5E63] text-white rounded-lg hover:bg-[#2d4a4d] transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Form Submitted Successfully!</h2>
          <p className="text-gray-600 mb-2">
            Thank you for completing the facial consent form.
          </p>
          <p className="text-gray-500 text-sm mb-6">
            感谢您填写面部护理同意书。
          </p>

          {appointmentInfo && (
            <div className="bg-[#F1F7F4] rounded-lg p-4 mb-6 text-left">
              <p className="text-sm font-medium text-[#3A5E63] mb-2">Your Appointment:</p>
              <p className="text-gray-700">{appointmentInfo.date}</p>
              <p className="text-gray-600 text-sm">{appointmentInfo.services.join(', ')}</p>
            </div>
          )}

          <div className="space-y-3">
            {user ? (
              <button
                onClick={() => navigate('/profile')}
                className="w-full px-6 py-2 bg-[#3A5E63] text-white rounded-lg hover:bg-[#2d4a4d] transition-colors"
              >
                Go to Profile
              </button>
            ) : (
              <button
                onClick={() => navigate('/')}
                className="w-full px-6 py-2 bg-[#3A5E63] text-white rounded-lg hover:bg-[#2d4a4d] transition-colors"
              >
                Return to Home
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Form state
  const isAlreadySigned = !!existingSignatureUrl;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-serif font-semibold text-[#3A5E63] mb-2">
            Facial Consent Form
          </h1>
          <p className="text-gray-500">面部护理同意书</p>
          <p className="text-gray-600 mt-2">
            Healing Lotus: Sound Therapy And Skin SPA
          </p>
        </div>

        {/* Appointment Info Banner */}
        {appointmentInfo && (
          <div className="bg-[#F1F7F4] border border-[#B2D8C5] rounded-lg p-4 mb-6">
            <p className="text-sm text-[#3A5E63] font-medium">Your upcoming appointment:</p>
            <p className="text-gray-700">{appointmentInfo.date}</p>
            <p className="text-gray-600 text-sm">{appointmentInfo.services.join(', ')}</p>
          </div>
        )}

        {/* Already Signed Notice */}
        {isAlreadySigned && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-green-700 font-medium">Form already submitted</p>
            </div>
            <p className="text-green-600 text-sm mt-1">
              You can update your information below. Your signature will remain locked.
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Form */}
        <FacialConsentForm
          initialData={existingFormData || undefined}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          signatureLocked={isAlreadySigned}
          existingSignatureUrl={existingSignatureUrl}
        />

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>Questions? Contact us at +1 (972) 998-0431</p>
          <p className="mt-1">4222 Trinity Mills Rd Suite 120, Dallas, TX 75024</p>
        </div>
      </div>
    </div>
  );
};

export default FacialConsentFormPage;
